// The client records grid (Clients › Records): every client's commercial record in one table, filled in by typing or
// by pasting a block copied from Google Sheets or Excel. Rows save together through dashboard_save_client_terms_bulk,
// each exactly as the one-client form would save it (rnrinfra migration 20261001144854_hub_today).
import {billingLabels,draftRequest,outcomeLabels,stageLabels,toDraft,type ClientTerms,type Stage,type TermsDraft} from './business-kpis-model';

export type GridClient={business_id:string;client_name:string;agency_id:string;agency:string;manager:string|null;relationship_status:string;
 terms:Partial<ClientTerms>&{stage:Stage;revision:number}};
export type Closer={id:string;name:string};
export type GridData={clients:GridClient[];closers:Record<string,Closer[]>};
export type Field=Exclude<keyof TermsDraft,'reason'>;
export type Kind='stage'|'billing'|'money'|'count'|'date'|'outcome'|'closer'|'text';
export type Column={key:Field;label:string;kind:Kind;width:number;aliases?:string[];max?:number};

export const COLUMNS:Column[]=[
 {key:'stage',label:'Stage',kind:'stage',width:160,aliases:['status']},
 {key:'billing_model',label:'How they pay',kind:'billing',width:180,aliases:['billing','billing model','pays']},
 {key:'monthly_fee',label:'Monthly fee',kind:'money',width:116,aliases:['fee','retainer','monthly']},
 {key:'price_per_lead',label:'Price per lead',kind:'money',width:116,aliases:['per lead','ppl','cost per lead','lead price']},
 {key:'monthly_lead_goal',label:'Lead goal a month',kind:'count',width:124,aliases:['lead goal','goal','monthly goal','leads a month','monthly lead goal','target']},
 {key:'paying_since',label:'Paying since',kind:'date',width:124,aliases:['start date','started paying','paying from']},
 {key:'trial_started_on',label:'Trial started',kind:'date',width:124,aliases:['trial start','trial started on']},
 {key:'trial_lead_goal',label:'Trial leads',kind:'count',width:104,aliases:['leads in the trial','trial goal','trial lead goal']},
 {key:'trial_ended_on',label:'Trial ended',kind:'date',width:124,aliases:['trial end','trial ended on']},
 {key:'trial_outcome',label:'Trial outcome',kind:'outcome',width:146,aliases:['outcome','how it ended']},
 {key:'closer_id',label:'Closer',kind:'closer',width:160,aliases:['closer name','sold by']},
 {key:'talked_on',label:'Talked on',kind:'date',width:124,aliases:['talked','closer talked']},
 {key:'closed_on',label:'Closed on',kind:'date',width:124,aliases:['closed','close date']},
 {key:'cancelled_on',label:'Cancelled on',kind:'date',width:124,aliases:['cancelled','canceled','cancel date','canceled on']},
 {key:'cancel_reason',label:'Cancel reason',kind:'text',width:210,aliases:['reason','why cancelled','cancellation reason'],max:1000},
 {key:'client_monthly_revenue',label:'Client’s revenue a month',kind:'money',width:150,aliases:['client revenue','revenue','client monthly revenue']},
 {key:'notes',label:'Notes',kind:'text',width:260,aliases:['note','comments'],max:4000},
];
const CLIENT_HEADERS=['client','client name','name','business','company','account'];

const squash=(s:string)=>s.toLowerCase().replace(/[’']/g,'').replace(/[^a-z0-9]+/g,' ').trim();
/** The column a spreadsheet header names, 'client' for the client name, or null. */
export function headerColumn(header:string):Field|'client'|null{
 const h=squash(header);
 if(!h)return null;
 if(CLIENT_HEADERS.includes(h))return 'client';
 return COLUMNS.find(c=>squash(c.label)===h||squash(c.key)===h||c.aliases?.some(a=>squash(a)===h))?.key??null;
}

// ---- Reading what is typed or pasted ----
type Read={value:string}|{error:string};
const STAGES:Record<string,Stage>={'':'unknown','not set':'unknown',unknown:'unknown',none:'unknown',
 trial:'trial','on trial':'trial',trialing:'trial',
 paying:'paying',paid:'paying',active:'paying',live:'paying',
 paused:'paused',pause:'paused','on hold':'paused',hold:'paused',
 cancelled:'cancelled',canceled:'cancelled',churned:'cancelled',lost:'cancelled',
 'trial ended':'trial_ended','trial didnt convert':'trial_ended','trial did not convert':'trial_ended','trial failed':'trial_ended','trial ended unsuccessful':'trial_ended'};
const BILLING:Record<string,string>={'':'','not set':'',none:'',
 monthly:'monthly','monthly fee':'monthly',retainer:'monthly','flat fee':'monthly',flat:'monthly',
 'per lead':'per_lead','pay per lead':'per_lead',ppl:'per_lead','price per lead':'per_lead',
 hybrid:'hybrid',both:'hybrid','monthly fee per lead':'hybrid','monthly per lead':'hybrid','retainer per lead':'hybrid'};
const OUTCOMES:Record<string,string>={'':'',converted:'converted',won:'converted',unsuccessful:'unsuccessful',failed:'unsuccessful',lost:'unsuccessful',abandoned:'abandoned',dropped:'abandoned'};
for(const [k,v] of Object.entries(stageLabels))STAGES[squash(v)]=k as Stage;
for(const [k,v] of Object.entries(billingLabels))BILLING[squash(v)]=k;
for(const [k,v] of Object.entries(outcomeLabels))OUTCOMES[squash(v)]=k;
for(const k of Object.keys(stageLabels))STAGES[squash(k)]=k as Stage;
for(const k of Object.keys(billingLabels))BILLING[squash(k)]=k;

const MONTHS=['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
const pad=(n:number)=>String(n).padStart(2,'0');
function isoDate(y:number,m:number,d:number):string|null{
 if(y<100)y+=2000;
 const t=new Date(Date.UTC(y,m-1,d));
 return t.getUTCFullYear()===y&&t.getUTCMonth()===m-1&&t.getUTCDate()===d&&y>=2000&&y<=2100?`${y}-${pad(m)}-${pad(d)}`:null;
}
/** 2026-09-01, 9/1/2026, 9/1/26, Sep 1, 2026, 1 September 2026: a calendar date as YYYY-MM-DD, or null. */
export function readDate(raw:string):string|null{
 const s=raw.trim().toLowerCase().replace(/(\d)(st|nd|rd|th)\b/g,'$1').replace(/,/g,' ').replace(/\s+/g,' ');
 let m=s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[t ].*)?$/);
 if(m)return isoDate(+m[1],+m[2],+m[3]);
 m=s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
 if(m)return isoDate(+m[3],+m[1],+m[2]);
 m=s.match(/^([a-z]{3})[a-z]*\.? (\d{1,2}) (\d{4})$/);
 if(m&&MONTHS.includes(m[1]))return isoDate(+m[3],MONTHS.indexOf(m[1])+1,+m[2]);
 m=s.match(/^(\d{1,2}) ([a-z]{3})[a-z]*\.? (\d{4})$/);
 if(m&&MONTHS.includes(m[2]))return isoDate(+m[3],MONTHS.indexOf(m[2])+1,+m[1]);
 return null;
}
/** $1,500.00 or 1500 as "1500"; negative, text or more than two decimals is an error. */
export function readMoney(raw:string):string|null{
 const s=raw.trim().replace(/[$\s,]/g,'').replace(/^usd/i,'');
 if(!/^\d+(\.\d{1,2})?$/.test(s))return null;
 return String(Number(s));
}
export function readCount(raw:string):string|null{
 const s=raw.trim().replace(/[,\s]/g,'').replace(/leads?$/i,'');
 return /^\d{1,6}$/.test(s)?String(Number(s)):null;
}

/** A value for a cell, as the database stores it, or why it can't be read. Closers are named people at the client's agency. */
export function readCell(col:Column,raw:string,closers:Closer[]=[]):Read{
 const s=raw.trim();
 switch(col.kind){
  case 'stage':{const v=STAGES[squash(s)];return v?{value:v}:{error:`“${s}” is not a stage. Use ${Object.values(stageLabels).join(', ')}.`};}
  case 'billing':{const v=BILLING[squash(s)];return v!==undefined?{value:v}:{error:`“${s}” is not a way to pay. Use Monthly fee, Per lead or Monthly fee + per lead.`};}
  case 'outcome':{const v=OUTCOMES[squash(s)];return v!==undefined?{value:v}:{error:`“${s}” is not a trial outcome. Use Converted, Unsuccessful or Abandoned.`};}
  case 'money':{if(!s)return {value:''};const v=readMoney(s);return v===null?{error:`“${s}” is not an amount in dollars.`}:{value:v};}
  case 'count':{if(!s)return {value:''};const v=readCount(s);return v===null?{error:`“${s}” is not a whole number.`}:{value:v};}
  case 'date':{if(!s)return {value:''};const v=readDate(s);return v===null?{error:`“${s}” is not a date. Use 2026-09-01 or 9/1/2026.`}:{value:v};}
  case 'closer':{
   if(!s||squash(s)==='none'||squash(s)==='no closer')return {value:''};
   const byId=closers.find(c=>c.id===s);if(byId)return {value:byId.id};
   const exact=closers.filter(c=>squash(c.name)===squash(s));
   const first=closers.filter(c=>squash(c.name).split(' ')[0]===squash(s));
   const hit=exact.length===1?exact[0]:first.length===1?first[0]:null;
   return hit?{value:hit.id}:{error:`No one called “${s}” at this client’s agency can be the closer.`};
  }
  default:{const max=col.max??1000;return s.length>max?{error:`Keep it under ${max} characters.`}:{value:s};}
 }
}

// ---- The grid's state ----
export type Edits=Record<string,Partial<Record<Field,string>>>;
export const original=(c:GridClient):TermsDraft=>toDraft({business_id:c.business_id,...c.terms} as ClientTerms);
export const cellValue=(c:GridClient,key:Field,edits:Edits)=>edits[c.business_id]?.[key]??original(c)[key];

/** A value as it compares: amounts, counts and dates as they would be saved, so "$1,500" equals 1500. */
function comparable(key:Field,v:string):string{
 const col=COLUMNS.find(c=>c.key===key)!;
 if(col.kind!=='money'&&col.kind!=='count'&&col.kind!=='date')return v.trim();
 const r=readCell(col,v);return 'value' in r?r.value:v.trim();
}
/** Edits that differ from what is saved. */
export function changedFields(c:GridClient,edits:Edits):Partial<Record<Field,string>>{
 const base=original(c),out:Partial<Record<Field,string>>={};
 for(const [k,v] of Object.entries(edits[c.business_id]??{}) as [Field,string][])if(v!==undefined&&comparable(k,v)!==comparable(k,base[k]))out[k]=v;
 return out;
}

/**
 * Why each changed client can't be saved, by column: the rules dashboard_save_client_terms applies, checked before
 * sending. Supabase fills the rest itself: today for a new trial, a cancellation or a start of paying.
 */
export function problems(clients:GridClient[],edits:Edits,closers:Record<string,Closer[]>,today:string):Record<string,Partial<Record<Field,string>>>{
 const out:Record<string,Partial<Record<Field,string>>>={};
 for(const c of clients){
  const changed=changedFields(c,edits);
  if(!Object.keys(changed).length)continue;
  const row:Partial<Record<Field,string>>={};
  for(const [k,v] of Object.entries(changed) as [Field,string][]){
   const col=COLUMNS.find(x=>x.key===k)!,r=readCell(col,v,closers[c.agency_id]);
   if('error' in r)row[k]=r.error;
   else if(col.kind==='date'&&r.value>today)row[k]='Dates can’t be in the future.';
  }
  const v=(k:Field)=>cellValue(c,k,edits).trim();
  if(v('stage')==='cancelled'&&!v('cancel_reason'))row.cancel_reason??='Give the reason for the cancellation.';
  if(v('stage')==='trial_ended'&&!['unsuccessful','abandoned'].includes(v('trial_outcome')))row.trial_outcome??='Say whether the trial was unsuccessful or abandoned.';
  if(Object.keys(row).length)out[c.business_id]=row;
 }
 return out;
}

/** The rows dashboard_save_client_terms_bulk takes: each changed client's whole record, at the revision it was read. */
export function saveRows(clients:GridClient[],edits:Edits,closers:Record<string,Closer[]>):{business_id:string;request:Record<string,string|number|null>}[]{
 return clients.flatMap(c=>{
  const changed=changedFields(c,edits);
  if(!Object.keys(changed).length)return [];
  const draft:TermsDraft={...original(c),reason:'Filled in on the client records grid'};
  for(const [k,v] of Object.entries(changed) as [Field,string][]){const r=readCell(COLUMNS.find(x=>x.key===k)!,v,closers[c.agency_id]);if('value' in r)draft[k]=r.value;}
  return [{business_id:c.business_id,request:draftRequest(draft,c.terms.revision)}];
 });
}

// ---- Pasting a block from a spreadsheet ----
/** Tab-separated text as Google Sheets and Excel copy it: quoted cells may hold tabs, line breaks and doubled quotes. */
export function parseTsv(text:string):string[][]{
 const s=text.replace(/\r\n?/g,'\n'),rows:string[][]=[];
 let row:string[]=[],cell='',quoted=false;
 for(let i=0;i<s.length;i++){
  const ch=s[i];
  if(quoted){if(ch==='"'){if(s[i+1]==='"'){cell+='"';i++;}else quoted=false;}else cell+=ch;continue;}
  if(ch==='"'&&cell===''){quoted=true;continue;}
  if(ch==='\t'){row.push(cell);cell='';continue;}
  if(ch==='\n'){row.push(cell);rows.push(row);row=[];cell='';continue;}
  cell+=ch;
 }
 if(cell!==''||row.length)rows.push([...row,cell]);
 while(rows.length&&rows[rows.length-1].every(c=>c.trim()===''))rows.pop();
 return rows;
}

const nameKey=(s:string)=>squash(s).replace(/\b(llc|inc|co|corp|company|the|ltd)\b/g,' ').replace(/\s+/g,' ').trim();
/** The grid row a pasted client name means: the same name, or the only name that contains it. */
export function matchClient(name:string,clients:GridClient[]):GridClient|null{
 const key=nameKey(name);
 if(!key)return null;
 const exact=clients.filter(c=>nameKey(c.client_name)===key);
 if(exact.length===1)return exact[0];
 const partial=clients.filter(c=>nameKey(c.client_name).includes(key)||key.includes(nameKey(c.client_name)));
 return partial.length===1?partial[0]:null;
}

export type PasteResult={edits:Edits;cells:number;clients:number;problems:string[];unmatched:string[];ignored:string[];skippedRows:number};
/**
 * Fills the grid from a pasted block. With a header row, columns go by their headers; without one, they start at the
 * cell that has focus (col -1 is the client name). With client names, rows go by name; without, they start at the
 * focused row. Blank pasted cells leave a value as it is. Cells that can't be read are listed and left alone.
 */
export function applyPaste(text:string,clients:GridClient[],at:{row:number;col:number},edits:Edits,closers:Record<string,Closer[]>):PasteResult{
 const block=parseTsv(text);
 const result:PasteResult={edits:{...edits},cells:0,clients:0,problems:[],unmatched:[],ignored:[],skippedRows:0};
 if(!block.length)return result;
 const width=Math.max(...block.map(r=>r.length));
 const heads=block[0].map(headerColumn),named=block[0].filter(c=>c.trim()).length;
 const hasHeader=heads.filter(Boolean).length>0&&heads.filter(Boolean).length*2>=named;
 const map:(Field|'client'|null)[]=hasHeader?heads:Array.from({length:width},(_,i)=>{const k=at.col+i;return k<0?(k===-1?'client':null):COLUMNS[k]?.key??null;});
 if(hasHeader)block[0].forEach((h,i)=>{if(h.trim()&&!heads[i])result.ignored.push(h.trim());});
 else if(at.col+width>COLUMNS.length)result.ignored.push(`${at.col+width-COLUMNS.length} ${at.col+width-COLUMNS.length===1?'column':'columns'} past Notes`);
 const body=hasHeader?block.slice(1):block;
 const nameAt=map.indexOf('client');
 const touched=new Set<string>();
 body.forEach((cells,k)=>{
  let target:GridClient|null;
  if(nameAt>=0){
   const name=(cells[nameAt]??'').trim();
   if(!name){if(cells.some(c=>c.trim()))result.skippedRows++;return;}
   target=matchClient(name,clients);
   if(!target){result.unmatched.push(name);return;}
  }else{
   target=clients[at.row+k]??null;
   if(!target){if(cells.some(c=>c.trim()))result.skippedRows++;return;}
  }
  const row={...result.edits[target.business_id]};
  cells.forEach((raw,i)=>{
   const key=map[i];
   if(!key||key==='client'||!raw.trim())return;
   const col=COLUMNS.find(c=>c.key===key)!;
   const r=readCell(col,raw,closers[target.agency_id]);
   if('error' in r){result.problems.push(`${target.client_name} · ${col.label}: ${r.error}`);return;}
   row[key]=r.value;result.cells++;touched.add(target.business_id);
  });
  result.edits[target.business_id]=row;
 });
 result.clients=touched.size;
 return result;
}

/** Clients with something still missing: no stage, or paying with no billing or no lead goal. */
export function missingSomething(c:GridClient,edits:Edits):boolean{
 const v=(k:Field)=>cellValue(c,k,edits).trim();
 const stage=v('stage');
 return stage==='unknown'||stage===''||((stage==='paying'||stage==='trial')&&!v('monthly_lead_goal'))||(stage==='paying'&&!v('billing_model'));
}
